/**
 * Shared image conversion utilities.
 * Two contexts: GCS upload (high quality) and AI generation input (smaller, base64).
 */

/**
 * Convert a File (PNG/JPEG/WebP) to WebP using Canvas API.
 * Used before uploading to GCS — high quality, large dimensions preserved.
 *
 * If the input is already WebP and within dimension limits, returns it as-is.
 * Falls back to JPEG if browser doesn't support WebP encoding.
 */
export async function convertFileToWebP(
    file: File,
    options?: { maxDimension?: number; quality?: number }
): Promise<{ file: File; width: number; height: number }> {
    const maxDim = options?.maxDimension ?? 5120;
    const quality = options?.quality ?? 0.92;

    const objectUrl = URL.createObjectURL(file);
    try {
        const { naturalWidth, naturalHeight } = await loadImage(objectUrl);

        // If already WebP and within dimension limits, return as-is
        if (file.type === 'image/webp' && naturalWidth <= maxDim && naturalHeight <= maxDim) {
            console.log(`Image already WebP and within ${maxDim}px limit, keeping as-is (${(file.size / 1024).toFixed(0)}KB)`);
            return { file, width: naturalWidth, height: naturalHeight };
        }

        // Calculate target dimensions
        let w = naturalWidth;
        let h = naturalHeight;
        if (w > maxDim || h > maxDim) {
            if (w > h) {
                h = Math.round(h * (maxDim / w));
                w = maxDim;
            } else {
                w = Math.round(w * (maxDim / h));
                h = maxDim;
            }
        }

        // Detect WebP support
        const { mime, ext } = getOutputFormat();

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas context unavailable');

        const img = await loadImage(objectUrl);
        ctx.drawImage(img, 0, 0, w, h);

        const blob = await canvasToBlob(canvas, mime, quality);
        const stem = file.name.replace(/\.[^.]+$/, '');
        const webpFile = new File([blob], `${stem}.${ext}`, { type: mime });

        console.log(
            `Image converted: ${file.name} (${(file.size / 1024).toFixed(0)}KB, ${naturalWidth}x${naturalHeight})` +
            ` → ${webpFile.name} (${(webpFile.size / 1024).toFixed(0)}KB, ${w}x${h})` +
            ` [${Math.round((1 - webpFile.size / file.size) * 100)}% reduction]`
        );

        return { file: webpFile, width: w, height: h };
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
}

/**
 * Convert an image URL to WebP base64 using Canvas API.
 * Used before sending to AI generation edge function — lower quality, smaller dimensions.
 *
 * Returns null if conversion fails (e.g. CORS), allowing fallback to original URL.
 */
export async function convertUrlToWebPBase64(
    imageUrl: string,
    options?: { maxDimension?: number; quality?: number }
): Promise<{ base64: string; mimeType: string; width: number; height: number } | null> {
    const maxDim = options?.maxDimension ?? 2048;
    const quality = options?.quality ?? 0.90;

    try {
        return await new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';

            img.onload = () => {
                let { naturalWidth: w, naturalHeight: h } = img;

                if (w > maxDim || h > maxDim) {
                    if (w > h) {
                        h = Math.round(h * (maxDim / w));
                        w = maxDim;
                    } else {
                        w = Math.round(w * (maxDim / h));
                        h = maxDim;
                    }
                }

                const canvas = document.createElement('canvas');
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext('2d');
                if (!ctx) { reject(new Error('Canvas context unavailable')); return; }

                ctx.drawImage(img, 0, 0, w, h);

                const { mime } = getOutputFormat();
                const dataUrl = canvas.toDataURL(mime, quality);
                const base64 = dataUrl.split(',')[1];

                console.log(
                    `Image resized for AI: ${img.naturalWidth}x${img.naturalHeight} → ${w}x${h},` +
                    ` base64: ${Math.round(base64.length / 1024)}KB`
                );

                resolve({ base64, mimeType: mime, width: w, height: h });
            };

            img.onerror = () => reject(new Error('Failed to load image for resize'));
            img.src = imageUrl;
        });
    } catch (err) {
        console.warn('Image conversion failed, will use original URL:', err);
        return null;
    }
}

// --- Internal helpers ---

function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = src;
    });
}

function canvasToBlob(canvas: HTMLCanvasElement, mime: string, quality: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => blob ? resolve(blob) : reject(new Error('Canvas toBlob failed')),
            mime,
            quality
        );
    });
}

/** Detect browser WebP encoding support, fallback to JPEG */
let _outputFormat: { mime: string; ext: string } | null = null;
function getOutputFormat(): { mime: string; ext: string } {
    if (_outputFormat) return _outputFormat;
    const c = document.createElement('canvas');
    c.width = 1; c.height = 1;
    const supportsWebP = c.toDataURL('image/webp').startsWith('data:image/webp');
    _outputFormat = supportsWebP
        ? { mime: 'image/webp', ext: 'webp' }
        : { mime: 'image/jpeg', ext: 'jpg' };
    return _outputFormat;
}
